/**
 * Number and time shapes shared by every CRM screen.
 *
 * All of them go through the Estonian locale rather than the UI language: the
 * operator reads these next to bank statements and register extracts, and a
 * thousands separator that changes with the interface language would make two
 * copies of the same figure look like two different figures.
 */

const NUM = new Intl.NumberFormat("et-EE");
const EUR0 = new Intl.NumberFormat("et-EE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
const EUR2 = new Intl.NumberFormat("et-EE", { style: "currency", currency: "EUR", minimumFractionDigits: 2 });

export function fmtNum(n: number): string {
  return NUM.format(n);
}

/** Cents in, euros out. `exact` keeps the cents; the default rounds them off. */
export function fmtMoney(cents: number, exact = false): string {
  return (exact ? EUR2 : EUR0).format(cents / 100);
}

/** Short money for a tile: thousands and millions collapse to one figure. */
export function fmtMoneyShort(cents: number): string {
  const eur = cents / 100;
  const abs = Math.abs(eur);
  if (abs >= 1_000_000) return `${NUM.format(Math.round(eur / 100_000) / 10)} mln €`;
  if (abs >= 10_000) return `${NUM.format(Math.round(eur / 1_000))} tuh €`;
  return EUR0.format(eur);
}

export function fmtPct(v: number, digits = 1): string {
  return `${v.toLocaleString("et-EE", { minimumFractionDigits: digits, maximumFractionDigits: digits })}%`;
}

/** Seconds as mm:ss, or h:mm:ss once a call runs past the hour. */
export function fmtDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return "-";
  const s = Math.max(0, Math.round(seconds));
  const mm = Math.floor(s / 60) % 60;
  const ss = s % 60;
  const hh = Math.floor(s / 3600);
  const pad = (n: number) => String(n).padStart(2, "0");
  return hh ? `${hh}:${pad(mm)}:${pad(ss)}` : `${pad(mm)}:${pad(ss)}`;
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString("et-EE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "-";
  return new Date(iso).toLocaleString("et-EE", { dateStyle: "short", timeStyle: "short" });
}

/**
 * The list-column stamp: clock time for today, day and month for this year, a
 * year for anything older. A full date on every row is noise when most of the
 * rows arrived this morning.
 */
export function fmtStamp(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString("et-EE", { hour: "2-digit", minute: "2-digit" });
  }
  if (d.getFullYear() === now.getFullYear()) {
    return d.toLocaleDateString("et-EE", { day: "numeric", month: "short" });
  }
  return d.toLocaleDateString("et-EE", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

/**
 * A file size a person reads rather than counts. Binary units, because that is
 * what the mail server and the file system both mean by a kilobyte.
 */
export function fmtBytes(bytes: number): string {
  if (bytes < 1024) return `${Math.max(0, Math.round(bytes))} B`;
  if (bytes < 1024 * 1024) return `${NUM.format(Math.round(bytes / 1024))} kB`;
  return `${NUM.format(Math.round((bytes / (1024 * 1024)) * 10) / 10)} MB`;
}

/** Whole days between a date and now, negative when the date is in the past. */
export function daysFromNow(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.round(ms / 86_400_000);
}
